import {BoltOutlined, CreditCardOutlined, PeopleOutline} from '@mui/icons-material';
import {FC} from 'react';
import {chargersApi, employeeApi, tagsApi} from 'src/api';
import {UserLinkedRail} from '../UserLinkedRail';
import {LinkedRow} from '../UserLinkedRail/UserLinkedRail.types';
import {CompanyLinkedRailProps} from './CompanyLinkedRail.types';

/** One row is enough to learn a total — the rail prints the count, not the list. */
const COUNT_ONLY = {page: 0, size: 1};

/**
 * What a company has, as numbers.
 *
 * A company is not a driver. It does not hold one charger and one card; it holds many, so the
 * driver's rail — which names a single charger and says "no charger" when there is none — answered
 * the wrong question for it. These are counts, taken from the same endpoints the tabs below use,
 * asking for a single row so the totals arrive without the lists.
 *
 * Each query is skipped without the permission that endpoint requires, and a skipped row shows its
 * empty state rather than a zero it never checked.
 */
export const CompanyLinkedRail: FC<CompanyLinkedRailProps> = ({
                                                                  businessId,
                                                                  companyId,
                                                                  canReadChargers,
                                                                  canReadRfid,
                                                                  canReadEmployees,
                                                              }) => {
    const chargers = chargersApi.useGetChargersQuery({
        data: {businessId, companyId},
        params: COUNT_ONLY,
    }, {skip: !companyId || !canReadChargers});

    const tags = tagsApi.useGetTagsQuery({
        data: {businessId, companyId, sortAsc: false, sortField: 'CREATED_AT', keyword: ''},
        params: COUNT_ONLY,
    }, {skip: !companyId || !canReadRfid});

    const employees = employeeApi.useSearchEmployeesQuery({
        data: {businessId, companyId},
        params: COUNT_ONLY,
    }, {skip: !companyId || !canReadEmployees});

    const asCount = (value?: number) => (typeof value === 'number' ? String(value) : undefined);

    const rows: LinkedRow[] = [
        {
            label: 'chargers',
            icon: <BoltOutlined/>,
            value: canReadChargers ? asCount(chargers.data?.page?.totalElements) : undefined,
            emptyLabel: 'noChargers',
        },
        {
            label: 'rfidsTabHeader',
            icon: <CreditCardOutlined/>,
            value: canReadRfid ? asCount(tags.data?.page?.totalElements) : undefined,
            emptyLabel: 'noCards',
        },
        {
            label: 'employees',
            icon: <PeopleOutline/>,
            value: canReadEmployees ? asCount(employees.data?.page?.totalElements) : undefined,
            emptyLabel: 'noEmployees',
        },
    ];

    return <UserLinkedRail rows={rows}/>;
};
